import React, { Fragment } from 'react';
import { func, arrayOf, shape } from 'prop-types';
import gql from 'graphql-tag';
import { Query } from 'react-apollo';
import ErrorBoundary from 'components/ErrorBoundary';
import {
  getRecipeSupplyItemIds,
  enrichSuppliesWithAffiliateLinks,
  processAffiliateLinks
} from './helpers';

const QUERY = gql`
  query recipeSupplyAffiliateLinks(
    $relatedItemIds: [String!]!
    $relatedItemType: String
  ) {
    affiliateLinks(
      relatedItemIds: $relatedItemIds
      relatedItemType: $relatedItemType
    ) {
      relatedItemId
      url
    }
  }
`;

/**
 * resolves and prepares the affiliate link data for recipe supply items.
 *
 * originally, this was a graphql field delegation, but now it's a separate
 * query to reduce coupling at the graphql layer.
 */
function RecipeSupplyAffiliateLinkDataProvider(props) {
  const { children, supplyGroups: baseSupplyGroups } = props;

  // Save errors on the client and catch on the server
  const errorPolicy = process.browser ? 'all' : 'none';

  if (!baseSupplyGroups) {
    return <Fragment>{children({ supplyGroups: [] })}</Fragment>;
  }

  const recipeSupplyItemIds = getRecipeSupplyItemIds(baseSupplyGroups);

  // our components always expect the affiliateLink prop to be present
  const baseSupplyGroupsWithAffiliateLinks = enrichSuppliesWithAffiliateLinks(
    baseSupplyGroups
  );

  if (recipeSupplyItemIds.length < 1) {
    return (
      <Fragment>
        {children({
          supplyGroups: baseSupplyGroupsWithAffiliateLinks
        })}
      </Fragment>
    );
  }

  const variables = {
    relatedItemIds: recipeSupplyItemIds,
    relatedItemType: 'RecipeSupplyItem'
  };

  return (
    <ErrorBoundary>
      <Query query={QUERY} variables={variables} errorPolicy={errorPolicy}>
        {({ data, loading, error }) => {
          // return children with original data while loading, and if anything
          // goes wrong
          if (error || loading) {
            return (
              <Fragment>
                {children({ supplyGroups: baseSupplyGroupsWithAffiliateLinks })}
              </Fragment>
            );
          }

          const supplyGroupsWithAffiliateLinks = enrichSuppliesWithAffiliateLinks(
            baseSupplyGroups,
            processAffiliateLinks(data.affiliateLinks)
          );

          return (
            <Fragment>
              {children({ supplyGroups: supplyGroupsWithAffiliateLinks })}
            </Fragment>
          );
        }}
      </Query>
    </ErrorBoundary>
  );
}

RecipeSupplyAffiliateLinkDataProvider.propTypes = {
  children: func.isRequired,
  supplyGroups: arrayOf(
    shape({
      // TBD
    })
  ).isRequired
};

export default RecipeSupplyAffiliateLinkDataProvider;
